import { useState, useCallback } from 'react';
import { useApi, useMutation } from '@/hooks/useApi';
import api from '@/api/client';
import {
  Brain,
  CheckCircle2,
  XCircle,
  Edit3,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Sparkles,
  Filter,
  CheckCheck,
  Zap,
  Target,
  TrendingUp,
  Search,
} from 'lucide-react';

// ── Types ───────────────────────────────────────────────────────────

interface Entity {
  id: number;
  name: string;
  code: string;
}

interface MappingSuggestion {
  id: number;
  entity_account_id: number;
  entity_account_code: string;
  entity_account_name: string;
  suggested_group_account_id: number | null;
  suggested_group_account_code: string;
  suggested_group_account_name: string;
  confidence: number;
  match_method: string;
  vector_similarity: number | null;
  llm_reasoning: string | null;
  llm_model: string | null;
  alternative_suggestions: Array<{ group_account_id: number; similarity: number; embedding_text: string }> | null;
  status: string;
  reviewed_by: number | null;
  reviewed_at: string | null;
  created_at: string | null;
}

interface MappingStats {
  total_entity_accounts: number;
  mapped_accounts: number;
  unmapped_accounts: number;
  ai_suggested_accounts: number;
  ai_auto_approved: number;
  human_corrected: number;
  mapping_rate_pct: number;
  avg_confidence: number;
  accuracy_rate: number;
  by_method: Record<string, number>;
  confidence_distribution: Record<string, number>;
  corrections_this_month: number;
}

interface GroupAccount {
  id: number;
  code: string;
  name: string;
  account_type: string;
}

// ── Confidence Badge ────────────────────────────────────────────────

function ConfidenceBadge({ confidence }: { confidence: number }) {
  let color = 'bg-red-100 text-red-700';
  let label = 'Low';
  if (confidence >= 90) {
    color = 'bg-green-100 text-green-700';
    label = 'High';
  } else if (confidence >= 70) {
    color = 'bg-yellow-100 text-yellow-700';
    label = 'Medium';
  }

  return (
    <div className="flex items-center gap-2">
      <div className="w-20 bg-gray-200 rounded-full h-2">
        <div
          className={`h-2 rounded-full ${confidence >= 90 ? 'bg-green-500' : confidence >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`}
          style={{ width: `${confidence}%` }}
        />
      </div>
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${color}`}>
        {confidence}% {label}
      </span>
    </div>
  );
}

// ── Method Badge ────────────────────────────────────────────────────

function MethodBadge({ method }: { method: string }) {
  const config: Record<string, { icon: typeof Brain; color: string; label: string }> = {
    exact_code: { icon: Target, color: 'bg-blue-100 text-blue-700', label: 'Exact Code' },
    exact_name: { icon: Target, color: 'bg-blue-100 text-blue-700', label: 'Exact Name' },
    historical: { icon: TrendingUp, color: 'bg-purple-100 text-purple-700', label: 'Historical' },
    vector: { icon: Sparkles, color: 'bg-cyan-100 text-cyan-700', label: 'Vector AI' },
    llm: { icon: Brain, color: 'bg-indigo-100 text-indigo-700', label: 'LLM Reasoning' },
    alias: { icon: Zap, color: 'bg-orange-100 text-orange-700', label: 'Alias' },
    rule: { icon: CheckCircle2, color: 'bg-gray-100 text-gray-700', label: 'Rule' },
    hybrid: { icon: Sparkles, color: 'bg-pink-100 text-pink-700', label: 'Hybrid' },
  };

  const cfg = config[method] || { icon: Brain, color: 'bg-gray-100 text-gray-600', label: method };
  const Icon = cfg.icon;

  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${cfg.color}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

// ── Suggestion Card ─────────────────────────────────────────────────

function SuggestionCard({
  suggestion,
  onAccept,
  onReject,
  onCorrect,
  selected,
  onToggleSelect,
}: {
  suggestion: MappingSuggestion;
  onAccept: (id: number) => void;
  onReject: (id: number) => void;
  onCorrect: (suggestion: MappingSuggestion) => void;
  selected: boolean;
  onToggleSelect: (id: number) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`border rounded-lg p-4 transition-all ${
      suggestion.status === 'accepted' ? 'bg-green-50 border-green-200' :
      suggestion.status === 'rejected' ? 'bg-red-50 border-red-200' :
      suggestion.status === 'corrected' ? 'bg-yellow-50 border-yellow-200' :
      'bg-white border-gray-200 hover:border-primary-300 hover:shadow-sm'
    }`}>
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        {suggestion.status === 'pending' && (
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelect(suggestion.id)}
            className="mt-1 h-4 w-4 text-primary-600 rounded"
          />
        )}

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* Entity Account -> Group Account */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-mono text-sm bg-gray-100 px-2 py-0.5 rounded">
              {suggestion.entity_account_code}
            </div>
            <span className="text-sm font-medium text-gray-900 truncate">
              {suggestion.entity_account_name}
            </span>
            <span className="text-gray-400">&rarr;</span>
            {suggestion.suggested_group_account_code ? (
              <>
                <div className="font-mono text-sm bg-primary-50 px-2 py-0.5 rounded text-primary-700">
                  {suggestion.suggested_group_account_code}
                </div>
                <span className="text-sm font-medium text-primary-900 truncate">
                  {suggestion.suggested_group_account_name}
                </span>
              </>
            ) : (
              <span className="text-sm text-red-500 italic">No match found</span>
            )}
          </div>

          {/* Badges row */}
          <div className="flex items-center gap-3 mt-2">
            <ConfidenceBadge confidence={suggestion.confidence} />
            <MethodBadge method={suggestion.match_method} />
            {suggestion.vector_similarity && (
              <span className="text-xs text-gray-500">
                cos={suggestion.vector_similarity.toFixed(3)}
              </span>
            )}
          </div>

          {/* Expandable reasoning */}
          {suggestion.llm_reasoning && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 mt-2"
            >
              {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              AI Reasoning
            </button>
          )}
          {expanded && suggestion.llm_reasoning && (
            <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm text-gray-700 border">
              <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                <Brain className="w-3 h-3" />
                {suggestion.llm_model || 'AI'}
              </div>
              {suggestion.llm_reasoning}
            </div>
          )}

          {/* Status indicator for reviewed items */}
          {suggestion.status !== 'pending' && (
            <div className={`mt-2 text-xs font-medium ${
              suggestion.status === 'accepted' ? 'text-green-600' :
              suggestion.status === 'rejected' ? 'text-red-600' :
              'text-yellow-600'
            }`}>
              {suggestion.status === 'accepted' && 'Accepted'}
              {suggestion.status === 'rejected' && 'Rejected'}
              {suggestion.status === 'corrected' && 'Corrected'}
            </div>
          )}
        </div>

        {/* Action buttons */}
        {suggestion.status === 'pending' && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => onAccept(suggestion.id)}
              className="p-1.5 rounded-lg text-green-600 hover:bg-green-50 transition-colors"
              title="Accept"
            >
              <CheckCircle2 className="w-5 h-5" />
            </button>
            <button
              onClick={() => onCorrect(suggestion)}
              className="p-1.5 rounded-lg text-yellow-600 hover:bg-yellow-50 transition-colors"
              title="Correct"
            >
              <Edit3 className="w-5 h-5" />
            </button>
            <button
              onClick={() => onReject(suggestion.id)}
              className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
              title="Reject"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Correction Modal ────────────────────────────────────────────────

function CorrectionModal({
  suggestion,
  groupAccounts,
  onSubmit,
  onClose,
}: {
  suggestion: MappingSuggestion;
  groupAccounts: GroupAccount[];
  onSubmit: (entityAccountId: number, groupAccountId: number, reason: string, suggestionId: number) => void;
  onClose: () => void;
}) {
  const [selectedGa, setSelectedGa] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [search, setSearch] = useState('');

  const filtered = groupAccounts.filter(
    (ga) =>
      ga.code.toLowerCase().includes(search.toLowerCase()) ||
      ga.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold">Correct Mapping</h3>
          <p className="text-sm text-gray-500 mt-1">
            <span className="font-mono">{suggestion.entity_account_code}</span>{' '}
            {suggestion.entity_account_name}
          </p>
        </div>

        <div className="p-6 flex-1 overflow-y-auto space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search Group Account
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by code or name..."
                className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
              />
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto border rounded-lg">
            {filtered.slice(0, 50).map((ga) => (
              <button
                key={ga.id}
                onClick={() => setSelectedGa(ga.id)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b last:border-b-0 ${
                  selectedGa === ga.id ? 'bg-primary-50 border-primary-200' : ''
                }`}
              >
                <span className="font-mono text-gray-500">{ga.code}</span>{' '}
                <span className="font-medium">{ga.name}</span>{' '}
                <span className="text-xs text-gray-400">({ga.account_type})</span>
              </button>
            ))}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Correction Reason (optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this mapping incorrect?"
              className="w-full border rounded-lg px-3 py-2 text-sm"
              rows={2}
            />
          </div>
        </div>

        <div className="p-6 border-t flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
            Cancel
          </button>
          <button
            onClick={() => selectedGa && onSubmit(suggestion.entity_account_id, selectedGa, reason, suggestion.id)}
            disabled={!selectedGa}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            Save Correction
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ───────────────────────────────────────────────────────

export default function AIMappingReview() {
  const [selectedEntityId, setSelectedEntityId] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [correctingSuggestion, setCorrectingSuggestion] = useState<MappingSuggestion | null>(null);

  // Fetch entities (API returns { items: [...] } or plain array)
  const { data: entities } = useApi<Entity[]>(
    () => api.get('/entities?limit=100&is_active=true').then((r) => {
      const d = r.data;
      return Array.isArray(d) ? d : d?.items ?? [];
    }),
    [],
  );

  // Fetch stats
  const { data: stats, refetch: refetchStats } = useApi<MappingStats>(
    () => api.get('/ai-mapper/stats').then((r) => r.data),
    [],
  );

  // Fetch suggestions for selected entity
  const { data: suggestions, loading: loadingSuggestions, refetch: refetchSuggestions } = useApi<MappingSuggestion[]>(
    () =>
      selectedEntityId
        ? api.get(`/ai-mapper/suggestions/${selectedEntityId}`, {
            params: { status: statusFilter || undefined },
          }).then((r) => r.data)
        : Promise.resolve([]),
    [selectedEntityId, statusFilter],
    { enabled: !!selectedEntityId },
  );

  // Fetch group accounts for correction modal
  const { data: groupAccounts } = useApi<GroupAccount[]>(
    () => api.get('/accounts/group').then((r) => r.data),
    [],
  );

  // Mutations
  const runMapping = useMutation<any, [number]>((entityId) =>
    api.post('/ai-mapper/run', { entity_id: entityId, use_llm: true }).then((r) => r.data)
  );

  const acceptMutation = useMutation<any, [number]>((id) =>
    api.post(`/ai-mapper/accept/${id}`).then((r) => r.data)
  );

  const rejectMutation = useMutation<any, [number]>((id) =>
    api.post(`/ai-mapper/reject/${id}`).then((r) => r.data)
  );

  const correctMutation = useMutation<any, [any]>((payload) =>
    api.post('/ai-mapper/correct', payload).then((r) => r.data)
  );

  const bulkReviewMutation = useMutation<any, [any]>((payload) =>
    api.post('/ai-mapper/bulk-review', payload).then((r) => r.data)
  );

  // Handlers
  const handleRunMapping = useCallback(async () => {
    if (!selectedEntityId) return;
    try {
      await runMapping.execute(selectedEntityId);
      refetchSuggestions();
      refetchStats();
    } catch {
      // error handled by hook
    }
  }, [selectedEntityId, runMapping, refetchSuggestions, refetchStats]);

  const handleAccept = useCallback(async (id: number) => {
    try {
      await acceptMutation.execute(id);
      refetchSuggestions();
      refetchStats();
    } catch {
      // handled
    }
  }, [acceptMutation, refetchSuggestions, refetchStats]);

  const handleReject = useCallback(async (id: number) => {
    try {
      await rejectMutation.execute(id);
      refetchSuggestions();
      refetchStats();
    } catch {
      // handled
    }
  }, [rejectMutation, refetchSuggestions, refetchStats]);

  const handleCorrect = useCallback(async (entityAccountId: number, groupAccountId: number, reason: string, suggestionId: number) => {
    try {
      await correctMutation.execute({
        suggestion_id: suggestionId,
        entity_account_id: entityAccountId,
        corrected_group_account_id: groupAccountId,
        correction_reason: reason || null,
      });
      setCorrectingSuggestion(null);
      refetchSuggestions();
      refetchStats();
    } catch {
      // handled
    }
  }, [correctMutation, refetchSuggestions, refetchStats]);

  const handleBulkAccept = useCallback(async () => {
    if (selectedIds.size === 0) return;
    try {
      await bulkReviewMutation.execute({
        suggestion_ids: Array.from(selectedIds),
        action: 'accept',
      });
      setSelectedIds(new Set());
      refetchSuggestions();
      refetchStats();
    } catch {
      // handled
    }
  }, [selectedIds, bulkReviewMutation, refetchSuggestions, refetchStats]);

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const pendingSuggestions = suggestions?.filter((s) => s.status === 'pending') || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Brain className="w-7 h-7 text-primary-600" />
            AI Mapping Review
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Review and approve AI-generated account mappings
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <div className="bg-white rounded-lg border p-4">
            <div className="text-2xl font-bold text-gray-900">{stats.total_entity_accounts}</div>
            <div className="text-xs text-gray-500">Total Accounts</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-2xl font-bold text-green-600">{stats.mapping_rate_pct}%</div>
            <div className="text-xs text-gray-500">Mapped</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-2xl font-bold text-primary-600">{stats.ai_suggested_accounts}</div>
            <div className="text-xs text-gray-500">AI Suggested</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-2xl font-bold text-green-600">{stats.ai_auto_approved}</div>
            <div className="text-xs text-gray-500">Auto-Approved</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-2xl font-bold text-yellow-600">{stats.human_corrected}</div>
            <div className="text-xs text-gray-500">Corrected</div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-2xl font-bold text-indigo-600">{stats.accuracy_rate}%</div>
            <div className="text-xs text-gray-500">Accuracy</div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Entity selector */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Entity:</label>
            <select
              value={selectedEntityId || ''}
              onChange={(e) => {
                setSelectedEntityId(Number(e.target.value) || null);
                setSelectedIds(new Set());
              }}
              className="border rounded-lg px-3 py-1.5 text-sm min-w-[200px]"
            >
              <option value="">Select entity...</option>
              {entities?.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.code} — {e.name}
                </option>
              ))}
            </select>
          </div>

          {/* Status filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border rounded-lg px-3 py-1.5 text-sm"
            >
              <option value="">All</option>
              <option value="pending">Pending Review</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
              <option value="corrected">Corrected</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 ml-auto">
            {selectedIds.size > 0 && (
              <button
                onClick={handleBulkAccept}
                disabled={bulkReviewMutation.loading}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                <CheckCheck className="w-4 h-4" />
                Accept Selected ({selectedIds.size})
              </button>
            )}

            <button
              onClick={handleRunMapping}
              disabled={!selectedEntityId || runMapping.loading}
              className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {runMapping.loading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              {runMapping.loading ? 'Running AI...' : 'Run AI Mapping'}
            </button>
          </div>
        </div>

        {/* Run result summary */}
        {runMapping.data && (
          <div className="mt-3 p-3 bg-primary-50 rounded-lg text-sm border border-primary-200">
            <div className="flex items-center gap-2 font-medium text-primary-800">
              <Sparkles className="w-4 h-4" />
              AI Mapping Complete — {runMapping.data.total_suggestions} suggestions
              ({runMapping.data.auto_approved} auto-approved) in {runMapping.data.processing_time_ms}ms
            </div>
            <div className="flex gap-4 mt-1 text-primary-600">
              <span>High: {runMapping.data.high_confidence}</span>
              <span>Medium: {runMapping.data.medium_confidence}</span>
              <span>Low: {runMapping.data.low_confidence}</span>
            </div>
          </div>
        )}
      </div>

      {/* Suggestions List */}
      <div className="space-y-3">
        {loadingSuggestions ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-primary-500" />
            <span className="ml-2 text-gray-500">Loading suggestions...</span>
          </div>
        ) : !selectedEntityId ? (
          <div className="text-center py-12 text-gray-500">
            <Brain className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Select an entity to view AI mapping suggestions</p>
          </div>
        ) : suggestions && suggestions.length > 0 ? (
          <>
            {/* Select all for pending */}
            {pendingSuggestions.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <input
                  type="checkbox"
                  checked={pendingSuggestions.every((s) => selectedIds.has(s.id))}
                  onChange={() => {
                    if (pendingSuggestions.every((s) => selectedIds.has(s.id))) {
                      setSelectedIds(new Set());
                    } else {
                      setSelectedIds(new Set(pendingSuggestions.map((s) => s.id)));
                    }
                  }}
                  className="h-4 w-4 text-primary-600 rounded"
                />
                Select all pending ({pendingSuggestions.length})
              </div>
            )}

            {suggestions.map((s) => (
              <SuggestionCard
                key={s.id}
                suggestion={s}
                onAccept={handleAccept}
                onReject={handleReject}
                onCorrect={setCorrectingSuggestion}
                selected={selectedIds.has(s.id)}
                onToggleSelect={toggleSelect}
              />
            ))}
          </>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-300" />
            <p>No suggestions found. Run AI Mapping to generate suggestions.</p>
          </div>
        )}
      </div>

      {/* Correction Modal */}
      {correctingSuggestion && groupAccounts && (
        <CorrectionModal
          suggestion={correctingSuggestion}
          groupAccounts={groupAccounts}
          onSubmit={handleCorrect}
          onClose={() => setCorrectingSuggestion(null)}
        />
      )}
    </div>
  );
}
